import type { ApiSchema } from "../apiSchema";
import { jsonBody, requestJson, withQuery } from "../http/httpClient";
import { readArray, readInteger, readNullableString, readObject, readString } from "../http/jsonDecoding";
import { readChangeSetSummary, type ChangeSetState, type ChangeSetSummary } from "./reviewDecoders";

/**
 * Change sets of the project content (`docs/components/content.md` D-5, D-17): open one
 * with edits, or commit more edits on the head the user saw. The web app proposes triple
 * patches to RDF items and whole process items; every edit of an existing item names the
 * version it was made on.
 */

/** Triples to add to and remove from one ontology module, as Turtle. */
export interface RdfPatch {
  readonly itemId: string;
  /** The module version (blob ID) the patch was made on. */
  readonly basedOn: string;
  readonly addTurtle: string;
  readonly removeTurtle: string;
}

const PAGE_SIZE = 100;

/** One edit that patches an ontology module's triples, for a commit. */
export function rdfPatchEdit(patch: RdfPatch): ApiSchema<"PatchRdfItemBody"> {
  return {
    operation: "patch_rdf",
    kind: "ontology_module",
    item_id: patch.itemId,
    based_on: patch.basedOn,
    add_turtle: patch.addTurtle,
    remove_turtle: patch.removeTurtle,
  };
}

const readChangeSetId = (body: unknown): string =>
  readString(readObject(readObject(body, "change set view")["change_set"], "change set"), "change_set_id");

/** One edit of a commit: create, replace, patch, remove or derive an item (D-8 to D-10, ADR-0037
 * D7), or a Workflow's edit with the copies made while editing it (WD-1+3 D-8b). */
export type ItemEdit = ApiSchema<"ChangeSetEditBody">;

/** What a proposal did: opened a change set, or found that every edit would store what the
 * latest version holds, so none opened. */
export type ProposalOutcome = { readonly kind: "proposed"; readonly changeSetId: string } | { readonly kind: "nothingToChange" };

const readProposalOutcome = (body: unknown): ProposalOutcome =>
  readObject(body, "proposal")["outcome"] === ("nothing_to_change" satisfies ApiSchema<"NothingToChangeBody">["outcome"])
    ? { kind: "nothingToChange" }
    : { kind: "proposed", changeSetId: readChangeSetId(body) };

/**
 * Open a change set with the edits as its first commit, unless they change nothing. A new
 * Workflow under a name a current one holds saves as its next version (`exact_items` is a
 * seed loader's, never the app's).
 */
export function proposeItemEdits(projectId: string, title: string, edits: readonly ItemEdit[]): Promise<ProposalOutcome> {
  return requestJson({
    method: "POST",
    path: `/projects/${encodeURIComponent(projectId)}/content/change-sets`,
    body: jsonBody({ title, description: "", edits: [...edits], exact_items: false } satisfies ApiSchema<"ChangeSetProposalBody">),
    decodeResponse: readProposalOutcome,
  });
}

/** Commit the edits on `expectedHead`; a head that moved is refused. */
export function commitItemEdits(changeSetId: string, expectedHead: string, edits: readonly ItemEdit[]): Promise<void> {
  return requestJson({
    method: "POST",
    path: `/content/change-sets/${encodeURIComponent(changeSetId)}/commits`,
    body: jsonBody({ expected_head: expectedHead, edits: [...edits], exact_items: false } satisfies ApiSchema<"ChangeSetCommitBody">),
    decodeResponse: () => undefined,
  });
}

/** The commit a change set's edits apply to now. */
export function readChangeSetHead(changeSetId: string, signal?: AbortSignal): Promise<string> {
  return requestJson({
    method: "GET",
    path: `/content/change-sets/${encodeURIComponent(changeSetId)}`,
    signal,
    decodeResponse: (body) => readString(readObject(body, "change set view"), "head"),
  });
}

/** A change set's head, and the item versions its own edits made there. */
export interface ChangeSetEdits {
  readonly head: string;
  /** The `VersionRef` each item it adds or changes has at its head. */
  readonly edited: ReadonlySet<string>;
}

/** What a change set edits (`readChangeSetHead` with its items): not what its base holds. */
export function readChangeSetEdits(changeSetId: string, signal?: AbortSignal): Promise<ChangeSetEdits> {
  return requestJson({
    method: "GET",
    path: `/content/change-sets/${encodeURIComponent(changeSetId)}`,
    signal,
    decodeResponse: (body) => {
      const view = readObject(body, "change set view");
      const versions = readArray(view["items"], "change set items", (item) => readNullableString(readObject(item, "change set item"), "new"));
      return { head: readString(view, "head"), edited: new Set(versions.filter((version) => version !== null)) };
    },
  });
}

/** The project's change sets in one state, newest first. */
export function listChangeSets(
  projectId: string,
  state: ChangeSetState,
  signal?: AbortSignal,
): Promise<readonly ChangeSetSummary[]> {
  return requestJson({
    method: "GET",
    path: withQuery(`/projects/${encodeURIComponent(projectId)}/content/change-sets`, { state }),
    signal,
    decodeResponse: (body) => readArray(body, "change sets", readChangeSetSummary),
  });
}

/** The `VersionRef` an item has at one commit of the project content (`null`: the latest), or `null`. */
export async function itemReferenceAt(
  projectId: string,
  commit: string | null,
  itemId: string,
  signal?: AbortSignal,
): Promise<string | null> {
  for (let offset = 0; ; offset += PAGE_SIZE) {
    const page = await requestJson({
      method: "GET",
      path: withQuery(`/projects/${encodeURIComponent(projectId)}/content/items`, {
        version: commit,
        offset,
        limit: PAGE_SIZE,
      }),
      signal,
      decodeResponse: (body) => {
        const listing = readObject(body, "item listing");
        return {
          total: readInteger(listing, "total_item_count"),
          items: readArray(listing["items"], "items", (item) => {
            const entry = readObject(item, "item");
            return { itemId: readString(entry, "item_id"), reference: readString(entry, "reference") };
          }),
        };
      },
    });
    const found = page.items.find((item) => item.itemId === itemId);
    if (found !== undefined) {
      return found.reference;
    }
    if (offset + PAGE_SIZE >= page.total) {
      return null;
    }
  }
}
